'use client'

import Link from 'next/link'
import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBell } from '@fortawesome/pro-solid-svg-icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import useApiClient from '@/lib/apis/clients/useApiClient'
import {
  GetNotificationSummary,
  MarkNotificationRead,
  NotificationDelivery,
} from '@/lib/apis/notifications'
import { useDetectClickOutside } from '@/shared/hooks/useDetectClickOutside'
import clsx from 'clsx'
import NcMarkdown from '@/shared/components/NcMarkdown'

export default function NotificationBell() {
  const api = useApiClient()
  const [open, setOpen] = useState(false)
  const query = useQuery(GetNotificationSummary.query(api))
  const markRead = useMutation({
    mutationFn: (deliveryId: number) =>
      MarkNotificationRead.execute(api, deliveryId),
    onSuccess: () => query.refetch(),
  })
  const ref = useDetectClickOutside<HTMLDivElement>({
    onTriggered: () => setOpen(false),
  })

  const latestDeliveryId = query.data?.notifications[0]?.deliveryId
  const unreadCount = query.data?.unreadCount ?? 0

  function toggleOpen() {
    const nextOpen = !open
    setOpen(nextOpen)
    if (nextOpen && latestDeliveryId) markRead.mutate(latestDeliveryId)
  }

  return (
    <div className="relative z-10" ref={ref}>
      <button
        type="button"
        aria-label="Notifications"
        onClick={toggleOpen}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-slate-700 transition hover:bg-slate-100 hover:text-blue-700"
      >
        <FontAwesomeIcon icon={faBell} />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex min-w-5 items-center justify-center rounded-full bg-orange-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 w-80 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
          <div className="border-b border-slate-100 px-4 py-3">
            <p className="font-sans text-sm font-semibold text-slate-900">
              Notifications
            </p>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {query.data?.notifications.length === 0 && (
              <p className="px-4 py-6 text-sm text-slate-500">
                No notifications
              </p>
            )}
            {query.data?.notifications.map((item) => (
              <NotificationRow key={item.deliveryId} item={item} />
            ))}
          </div>
          <Link
            href="/portal/notifications"
            onClick={() => setOpen(false)}
            className="block border-t border-slate-100 px-4 py-3 text-center text-sm font-semibold text-blue-700 hover:bg-blue-50"
          >
            View all
          </Link>
        </div>
      )}
    </div>
  )
}

function NotificationRow({ item }: { item: NotificationDelivery }) {
  const content = (
    <div
      className={clsx(
        'border-b border-slate-100 px-4 py-3 text-left transition last:border-b-0 hover:bg-slate-50',
        !item.read && 'bg-orange-50',
      )}
    >
      <div className="flex items-start gap-2">
        {!item.read && (
          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-orange-500" />
        )}
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-sm font-semibold text-slate-900">
            {item.title}
          </p>
          <NcMarkdown className="mt-1 line-clamp-3 text-xs leading-5 text-slate-600">
            {item.body}
          </NcMarkdown>
        </div>
      </div>
    </div>
  )

  if (item.link) return <Link href={item.link}>{content}</Link>
  return content
}
